import { useState, useEffect, FC } from 'react';
import { ExternalLink } from 'lucide-react';
import { toast } from 'react-toastify';
import { RadixTable, TColumn } from '@features/ui';
import { getCouponCodeTransactions } from '@src/services/coupons';
import { TCouponCodeTransaction } from '@src/types';

const TRANSACTION_COLUMNS: TColumn<TCouponCodeTransaction>[] = [
  {
    title: 'User',
    render: (t) => (
      <span className="whitespace-nowrap">
        {t?.username} ({t?.first_name} {t?.last_name})
      </span>
    ),
  },
  {
    title: 'Invoice',
    render: (t) => <span>{t?.display_invoice_id}</span>,
  },
  {
    title: 'Date/Time',
    render: (t) => {
      const date = t?.paid_at ? new Date(t.paid_at) : new Date();
      const formatted = date.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });

      return <span className="whitespace-nowrap">{formatted}</span>;
    },
  },
  {
    title: 'Receipt#',
    render: (t) => <span>{t?.receipt_id}</span>,
  },
  {
    title: 'Amount',
    className: 'text-right',
    render: (t) => <span className="font-medium">${t?.amount}</span>,
  },
  {
    title: 'Discount',
    className: 'text-right',
    render: (t) => <span>${t?.discount_amount}</span>,
  },
];

interface TransactionPanelProps {
  codeId?: number;
}

export const TransactionPanel: FC<TransactionPanelProps> = ({ codeId }) => {
  const [transactions, setTransactions] = useState<TCouponCodeTransaction[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [totalRevenue, setTotalRevenue] = useState('0.00');

  useEffect(() => {
    if (!codeId) return;

    setLoading(true);
    getCouponCodeTransactions(codeId, { page: 1, size: 100 })
      .then((res) => {
        const data = res.data;

        setTransactions(data.transactions ?? []);
        setTotalRevenue(data.total_revenue ?? '0.00');
      })
      .catch((err) => {
        console.error('Failed to load transactions:', err);
        toast.error('Failed to load transaction details');
      })
      .finally(() => setLoading(false));
  }, [codeId]);

  if (loading) {
    return (
      <div className="py-3 px-4">
        <div className="text-sm text-gray-500">Loading transactions...</div>
      </div>
    );
  }

  return (
    <div className="py-3 px-4">
      <div className="flex items-center justify-between mb-3">
        <button
          className="flex items-center gap-1.5 text-xs font-medium text-blue hover:underline"
          type="button"
        >
          <ExternalLink size={12} />
          Open in Payments Report
        </button>
        <span className="text-xs font-medium">
          Total revenue: <span>${totalRevenue}</span>
        </span>
      </div>
      <RadixTable columns={TRANSACTION_COLUMNS} data={transactions} />
    </div>
  );
};
